import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import AppLayout from '@cloudscape-design/components/app-layout';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FileUpload from '@cloudscape-design/components/file-upload';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Link from '@cloudscape-design/components/link';
import Multiselect, { MultiselectProps } from '@cloudscape-design/components/multiselect';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Textarea from '@cloudscape-design/components/textarea';
import { beginLogin, getAlias, getIdToken } from '../auth';
import { AppHeader } from '../components/AppHeader';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SideNav } from '../components/SideNav';
import { APP_HEADER_ID, MAX_ATTACHMENTS, MAX_UPLOAD_BYTES, SUPERREADER_SCOPE, SUPPORT_CONTACT } from '../constants';
import { orderScopes, scopeLabel } from '../utils/format';
import { AttachmentInput, createReport, ForbiddenError, uploadAttachment, UnauthorizedError } from '../api';
import { loadCapabilities } from '../capabilities';
import { clearReportsList } from './reportsListCache';
import { navigate, reportDetailPath, uploadPath } from '../router';

const MAX_UPLOAD_GB = Math.round(MAX_UPLOAD_BYTES / (1024 * 1024 * 1024));

type Phase = 'loading' | 'ready' | 'error';

function withSupport(message: string): string {
  return `${message} ${SUPPORT_CONTACT}`;
}

function attachmentFor(file: File): AttachmentInput {
  return { filename: file.name, contentType: file.type || 'application/octet-stream' };
}

interface SubmitResult {
  type: 'success' | 'error';
  text: string;
  reportId?: string;
}

export function UploadPage() {
  const [phase, setPhase] = useState<Phase>('loading');
  const [statusMessage, setStatusMessage] = useState('Loading…');
  const [alias, setAlias] = useState('');
  const [authenticated, setAuthenticated] = useState(false);

  const [availableScopes, setAvailableScopes] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<readonly MultiselectProps.Option[]>([]);
  const [files, setFiles] = useState<File[]>([]);

  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<SubmitResult | null>(null);

  const bootstrapped = useRef(false);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    void (async () => {
      const token = await getIdToken();
      if (!token) {
        await beginLogin();
        return;
      }
      setAlias(getAlias());
      setAuthenticated(true);
      try {
        const caps = await loadCapabilities();
        setAvailableScopes(caps.availableScopes);
        setPhase('ready');
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          await beginLogin();
          return;
        }
        setPhase('error');
        setStatusMessage(withSupport(err instanceof Error ? err.message : 'Could not load your scopes.'));
      }
    })();
  }, []);

  const oversized = files.filter((file) => file.size > MAX_UPLOAD_BYTES);
  const tooManyFiles = files.length > MAX_ATTACHMENTS;

  const titleError = submitted && !title.trim() ? 'Enter a title.' : undefined;
  const scopeError = submitted && selectedScopes.length === 0 ? 'Choose at least one scope.' : undefined;
  const fileError = tooManyFiles
    ? `You can attach at most ${MAX_ATTACHMENTS} files. Remove ${files.length - MAX_ATTACHMENTS} to continue.`
    : oversized.length > 0
      ? `These files exceed the ${MAX_UPLOAD_GB} GB per-file limit: ${oversized
          .map((file) => file.name)
          .join(', ')}.`
      : undefined;

  const resetForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setSelectedScopes([]);
    setFiles([]);
    setSubmitted(false);
  }, []);

  const onSubmit = useCallback(async () => {
    setSubmitted(true);
    setResult(null);

    const trimmedTitle = title.trim();
    const hasOversize = files.some((file) => file.size > MAX_UPLOAD_BYTES);
    if (!trimmedTitle || selectedScopes.length === 0 || files.length > MAX_ATTACHMENTS || hasOversize) {
      return;
    }

    setUploading(true);
    try {
      const scopes = selectedScopes.map((option) => option.value!).filter(Boolean);
      const created = await createReport({
        title: trimmedTitle,
        scopes,
        body: description.trim() || undefined,
        attachments: files.map(attachmentFor),
      });

      const uploads = created.attachmentUploads ?? [];
      const failed: string[] = [];
      for (let i = 0; i < uploads.length; i++) {
        const file = files[i];
        if (!file) continue;
        try {
          await uploadAttachment(uploads[i].url, file);
        } catch {
          failed.push(file.name);
        }
      }

      clearReportsList();

      if (failed.length > 0) {
        setResult({
          type: 'error',
          text: withSupport(
            `The report was created but these files failed to upload: ${failed.join(', ')}. ` +
              'You can re-upload by creating the report again.',
          ),
          reportId: created.reportId,
        });
      } else {
        setResult({
          type: 'success',
          text: files.length > 0 ? 'Your report and its attachments were uploaded.' : 'Your report was created.',
          reportId: created.reportId,
        });
        resetForm();
      }
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        await beginLogin();
        return;
      }
      const text =
        err instanceof ForbiddenError
          ? 'You are not authorized to upload reports for the selected scope(s).'
          : err instanceof Error
            ? err.message
            : 'Could not upload the report.';
      setResult({ type: 'error', text: withSupport(text) });
    } finally {
      setUploading(false);
    }
  }, [title, description, selectedScopes, files, resetForm]);

  const scopeOptions: MultiselectProps.Option[] = orderScopes(availableScopes).map((scope) => ({
    label: scopeLabel(scope),
    value: scope,
  }));

  return (
    <>
      <AppHeader alias={alias} authenticated={authenticated} />
      <AppLayout
        headerSelector={`#${APP_HEADER_ID}`}
        toolsHide
        navigation={<SideNav />}
        breadcrumbs={
          <Breadcrumbs
            items={[
              { text: 'Reports', href: '/' },
              { text: 'Upload report', href: uploadPath() },
            ]}
          />
        }
        content={
          <ContentLayout
            header={
              <Header variant="h1" description="Upload a report document and any related files.">
                Upload report
              </Header>
            }
          >
            {phase === 'loading' && <StatusIndicator type="loading">{statusMessage}</StatusIndicator>}
            {phase === 'error' && <StatusIndicator type="error">{statusMessage}</StatusIndicator>}
            {phase === 'ready' && (
              <form onSubmit={(e) => e.preventDefault()}>
                <Form
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button variant="link" disabled={uploading} onClick={resetForm} data-testid="upload-reset">
                        Clear
                      </Button>
                      <Button
                        variant="primary"
                        loading={uploading}
                        onClick={() => void onSubmit()}
                        data-testid="upload-submit"
                      >
                        Upload
                      </Button>
                    </SpaceBetween>
                  }
                >
                  <SpaceBetween size="l">
                    {result && (
                      <Alert type={result.type} dismissible onDismiss={() => setResult(null)}>
                        <SpaceBetween size="xs">
                          <span>{result.text}</span>
                          {result.reportId && (
                            <Link
                              onFollow={(e) => {
                                e.preventDefault();
                                navigate(reportDetailPath(result.reportId!));
                              }}
                              href={reportDetailPath(result.reportId)}
                            >
                              View the report
                            </Link>
                          )}
                        </SpaceBetween>
                      </Alert>
                    )}

                    <Container header={<Header variant="h2">Details</Header>}>
                      <SpaceBetween size="l">
                        <FormField label="Title" errorText={titleError}>
                          <Input
                            value={title}
                            onChange={({ detail }) => setTitle(detail.value)}
                            placeholder=""
                            ariaLabel="Report title"
                            data-testid="upload-title"
                          />
                        </FormField>

                        <FormField
                          label={
                            <span>
                              Description <i>- optional</i>
                            </span>
                          }
                          description="A short summary of what this report covers."
                        >
                          <Textarea
                            value={description}
                            onChange={({ detail }) => setDescription(detail.value)}
                            ariaLabel="Report description"
                            data-testid="upload-description"
                          />
                        </FormField>

                        <FormField
                          label="Scopes"
                          description="Who can see this report. You can only file under scopes you're authorized for."
                          errorText={scopeError}
                        >
                          <Multiselect
                            selectedOptions={selectedScopes}
                            onChange={({ detail }) => {
                              const picked = detail.selectedOptions;
                              const justPickedAll =
                                picked.some((option) => option.value === SUPERREADER_SCOPE) &&
                                !selectedScopes.some((option) => option.value === SUPERREADER_SCOPE);
                              setSelectedScopes(justPickedAll ? scopeOptions : picked);
                            }}
                            options={scopeOptions}
                            placeholder="Choose scope(s)"
                            empty="No scopes are available to you."
                            ariaLabel="Report scopes"
                          />
                        </FormField>
                      </SpaceBetween>
                    </Container>

                    <Container header={<Header variant="h2">Files</Header>}>
                      <FormField
                        label={
                          <span>
                            Attachments <i>- optional</i>
                          </span>
                        }
                        errorText={fileError}
                      >
                        <FileUpload
                          multiple
                          value={files}
                          onChange={({ detail }) => setFiles(detail.value)}
                          i18nStrings={{
                            uploadButtonText: (multiple) => (multiple ? 'Choose files' : 'Choose file'),
                            dropzoneText: (multiple) =>
                              multiple ? 'Drop files to upload' : 'Drop file to upload',
                            removeFileAriaLabel: (index) => `Remove file ${index + 1}`,
                            limitShowFewer: 'Show fewer files',
                            limitShowMore: 'Show more files',
                            errorIconAriaLabel: 'Error',
                          }}
                          showFileSize
                          showFileLastModified
                          constraintText={`Report documents, plus any related files. Up to ${MAX_ATTACHMENTS} files, ${MAX_UPLOAD_GB} GB each.`}
                          tokenLimit={20}
                        />
                      </FormField>
                    </Container>
                  </SpaceBetween>
                </Form>
              </form>
            )}
          </ContentLayout>
        }
      />
    </>
  );
}
